import { useEffect, useState } from 'react';
import {
  LOADING_TIPS,
  LOADING_TIP_DELAY_MS,
  LOADING_TIP_ROTATE_INTERVAL_MS,
  type LoadingTip,
  acquireLoadingTip,
  retainLoadingTip,
  releaseLoadingTip,
  noteHintVisible,
  rotateSharedTip,
} from '@/constants/loadingTips';

/**
 * ローディング中に「便利な使い方 Tips」を表示する共通コンポーネント。
 *
 * レイアウト方針:
 * - **中央にヒント（主役）** を大きめに見せる。
 * - **ローディング中である表示は控えめ**に。画面下に「読み込み中」→「.」→「..」→「...」を
 *   繰り返すシンプルなテキストだけを出す（大きなスピナーは置かない）。
 *
 * 表示ポリシー（Issue #205）:
 * - **チラつき防止**: `delayMs`（既定 250ms）経過するまで何も描画しない。実ロードが
 *   `delayMs` 未満で終わる速い局面では一切出さず、点滅を防ぐ（最低表示時間の水増しはしない）。
 * - **読ませる（Tips ローテーション）**: `rotate` を有効にすると `rotateIntervalMs` ごとに
 *   別の Tip へ切り替える。AI 生成など元々数秒以上かかる局面向け。
 *
 * アクセシビリティ: 装飾的な待機表示。`role="status"` + `aria-live="polite"` で読み込み中で
 * あることだけ伝え、フォーカスは奪わない。
 */
interface LoadingTipsProps {
  /** ヒント・ローディング表示までの遅延(ms)。チラつき防止。既定 250ms */
  delayMs?: number;
  /** Tips をローテーションするか（長時間待ち向け）。既定 false */
  rotate?: boolean;
  /** ローテーション間隔(ms)。既定 6000ms */
  rotateIntervalMs?: number;
  /** 画面高さいっぱいで中央寄せするか（ルート fallback 向け）。既定 true */
  fullScreen?: boolean;
  /** 追加クラス */
  className?: string;
}

/** 「読み込み中」＋末尾ドットの繰り返し表示（. → .. → ... → 空）。 */
function LoadingIndicator() {
  const [dots, setDots] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setDots((d) => (d + 1) % 4), 450);
    return () => clearInterval(id);
  }, []);
  return (
    <p
      role="status"
      aria-live="polite"
      className="text-xs text-text-400 tabular-nums"
    >
      読み込み中<span aria-hidden="true">{'.'.repeat(dots)}</span>
    </p>
  );
}

export default function LoadingTips({
  delayMs = LOADING_TIP_DELAY_MS,
  rotate = false,
  rotateIntervalMs = LOADING_TIP_ROTATE_INTERVAL_MS,
  fullScreen = true,
  className = '',
}: LoadingTipsProps) {
  // 連続ローダーでは直前と同じ Tip を使い回し、continuation なら遅延なしで即表示する
  // （毎回ランダムに切り替わって見える／間の空白チラつきを防ぐ）。
  const [initial] = useState(acquireLoadingTip);
  const [shown, setShown] = useState(initial.continuation);
  const [tip, setTip] = useState<LoadingTip>(initial.tip);
  const [fading, setFading] = useState(false);

  // delayMs 経過後に初めて表示する（速いロードでのチラつき防止）。
  // すでに表示中（continuation で即表示）なら遅延不要。
  useEffect(() => {
    if (shown) return;
    const id = setTimeout(() => setShown(true), delayMs);
    return () => clearTimeout(id);
  }, [delayMs, shown]);

  // 活性カウント管理（連続表示判定に使う）。マウント中は「表示継続中」とみなされ、
  // 次にマウントする別ローダーが同じ Tip を使い回す。
  useEffect(() => {
    retainLoadingTip();
    return () => releaseLoadingTip();
  }, []);

  // ヒントが実際に見えた瞬間を記録（最低表示時間の起点。複数ローダー間で共有）。
  useEffect(() => {
    if (shown) noteHintVisible();
  }, [shown]);

  // 長時間待ち向け: 一定間隔で別の Tip にフェード切替
  useEffect(() => {
    if (!rotate || !shown || LOADING_TIPS.length <= 1) return;
    let fadeTimer: ReturnType<typeof setTimeout> | undefined;
    const id = setInterval(() => {
      setFading(true);
      fadeTimer = setTimeout(() => {
        // 共有 Tip を回転させる（他ローダーの継続表示とも揃う）
        setTip(rotateSharedTip());
        setFading(false);
      }, 300);
    }, rotateIntervalMs);
    return () => {
      clearInterval(id);
      if (fadeTimer) clearTimeout(fadeTimer);
    };
  }, [rotate, shown, rotateIntervalMs]);

  if (!shown) {
    // 遅延中はレイアウトだけ確保して何も見せない（チラつき防止）
    return fullScreen ? <div className="min-h-screen" aria-hidden="true" /> : null;
  }

  // 中央: ヒント（主役）
  const hint = (
    <div
      className={`mx-auto max-w-sm text-center transition-opacity duration-300 ${fading ? 'opacity-0' : 'opacity-100'}`}
    >
      <p className="mb-2 text-xs font-semibold tracking-wide text-primary-500">
        💡 ヒント
      </p>
      {tip && (
        <>
          <p className="mb-2 text-base font-bold text-text-800">{tip.label}</p>
          <p className="text-sm leading-relaxed text-text-600">{tip.detail}</p>
        </>
      )}
    </div>
  );

  if (fullScreen) {
    // 画面いっぱい: ヒントを中央（flex-1）、ローディング表示を最下部に配置
    return (
      <div className={`flex min-h-screen flex-col ${className}`}>
        <div className="flex flex-1 items-center justify-center px-6 py-10">
          {hint}
        </div>
        <div className="flex justify-center pb-8">
          <LoadingIndicator />
        </div>
      </div>
    );
  }

  // インライン（AI 生成中など）: ヒントの下にローディング表示
  return (
    <div className={`flex flex-col items-center gap-3 py-6 ${className}`}>
      {hint}
      <LoadingIndicator />
    </div>
  );
}
